import { Injectable } from '@angular/core';

import { buildExcelBlob, normalizeExcelSpec } from '../core/excel/excel-export.builder';
import { getExcelFile, saveExcelFile } from '../core/excel/excel-file-store';
import {
    EXCEL_ERROR_CODE,
    ExcelFileCreateResult,
    ExcelFileInfo,
    ExcelFileRecord,
} from '../core/excel/excel-export.types';

/**
 * Сервис клиентской сборки Excel-файлов для AI-чата.
 * Собирает `.xlsx` в памяти, кладёт его в хранилище браузера и отдаёт агенту короткий fileId.
 */
@Injectable({ providedIn: 'root' })
export class AgentExcelFileService {
    /**
     * Валидирует аргументы агента, собирает файл и сохраняет его на клиенте.
     * Возвращает компактные сведения о файле либо структурированную ошибку для агента.
     */
    public async createFile(args: Record<string, unknown>): Promise<ExcelFileCreateResult> {
        const normalized = normalizeExcelSpec(args);

        if (normalized.status === 'error') {
            return { status: 'error', code: normalized.code, message: normalized.message };
        }

        const { spec } = normalized;

        try {
            const blob = await buildExcelBlob(spec);
            const record: ExcelFileRecord = {
                id: createFileId(),
                fileName: spec.fileName,
                sheetName: spec.sheetName,
                rowCount: spec.rows.length,
                columnCount: spec.columnCount,
                size: blob.size,
                createdAt: Date.now(),
                blob,
            };

            await saveExcelFile(record);

            return { status: 'ok', info: toFileInfo(record) };
        } catch (error) {
            console.error('Failed to build excel file:', error);

            return {
                status: 'error',
                code: EXCEL_ERROR_CODE.generationFailed,
                message: error instanceof Error
                    ? `Не удалось собрать Excel-файл: ${error.message}`
                    : 'Не удалось собрать Excel-файл.',
            };
        }
    }

    /** Возвращает сохранённый файл по id или null, если он не найден. */
    public getFile(id: string): Promise<ExcelFileRecord | null> {
        return getExcelFile(id);
    }

    /**
     * Запускает скачивание файла пользователем.
     * @returns `false`, если файл больше недоступен (истёк срок хранения или потерян).
     */
    public async downloadFile(id: string): Promise<boolean> {
        const record = await getExcelFile(id);

        if (!record) {
            return false;
        }

        triggerBlobDownload(record.blob, record.fileName);
        return true;
    }
}

/** Человекочитаемый размер файла для карточки блока. */
export function formatExcelFileSize(size: number): string {
    if (!Number.isFinite(size) || size <= 0) {
        return '0 Б';
    }

    if (size < 1024) {
        return `${size} Б`;
    }

    if (size < 1024 * 1024) {
        return `${(size / 1024).toFixed(1)} КБ`;
    }

    return `${(size / (1024 * 1024)).toFixed(1)} МБ`;
}

function toFileInfo(record: ExcelFileRecord): ExcelFileInfo {
    return {
        fileId: record.id,
        fileName: record.fileName,
        sheetName: record.sheetName,
        rowCount: record.rowCount,
        columnCount: record.columnCount,
        size: record.size,
    };
}

function createFileId(): string {
    const random = Math.random().toString(36).slice(2, 8);
    return `xlsx-${Date.now().toString(36)}-${random}`;
}

function triggerBlobDownload(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.rel = 'noopener';
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    link.remove();

    // Даём браузеру начать скачивание, затем освобождаем ссылку.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
